import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StageFormModal } from "./StageFormModal";
import {
  Loader2,
  Plus,
  Trash2,
  AlertTriangle,
  AlertCircle,
  Edit,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  opportunityAPI,
  OpportunityStage,
  StageStagnationConfig,
  StageVisibilityConfig,
} from "@/services/opportunityService";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface CreateStageModalProps {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  pipelineId?: string;
  onStageCreated?: () => void;
  onStageUpdated?: () => void;
  onStageDeleted?: () => void;
}

const STAGE_TYPE_TO_LEVEL: Record<string, number> = {
  start: 0,
  progress: 1,
  closed_won: 2,
  closed_lost: 2,
  custom: 1,
};

const LEVEL_TO_STAGE_TYPES: Record<number, string[]> = {
  0: ["start"],
  1: ["progress", "custom"],
  2: ["closed_won", "closed_lost"],
};

const LEVEL_GROUPS = [
  { label: "Inicial", level: 0, stage_types: ["start"] },
  { label: "En Progreso", level: 1, stage_types: ["progress", "custom"] },
  { label: "Finalizada", level: 2, stage_types: ["closed_won", "closed_lost"] },
];

const getStageTypeFromLevel = (level: number, stageName?: string): string => {
  const name = stageName?.toLowerCase() || "";
  const types = LEVEL_TO_STAGE_TYPES[level];

  if (level === 2) {
    // Determinar si es ganada o perdida según el nombre
    return name.includes("perdida") || name.includes("lost") ? "closed_lost" : "closed_won";
  }

  return types?.[0] || "progress";
};

const getStageButtonText = (stageType: string): string => {
  switch (stageType) {
    case "start":
      return "Inicial";
    case "progress":
    case "custom":
      return "En progreso";
    case "closed_won":
      return "Finalizada";
    case "closed_lost":
      return "Perdida";
    default:
      return "En progreso";
  }
};

export function CreateStageModal({
  isOpen,
  onClose,
  slug,
  pipelineId,
  onStageCreated,
  onStageUpdated,
  onStageDeleted,
}: CreateStageModalProps) {
  const { toast } = useToast();
  const [stages, setStages] = useState<OpportunityStage[]>([]);
  const [loading, setLoading] = useState({
    stages: false,
    create: false,
    delete: false,
  });
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingStage, setEditingStage] = useState<OpportunityStage | null>(
    null,
  );
  const [selectedGroupLevel, setSelectedGroupLevel] = useState<number>(0);
  const [stageToDelete, setStageToDelete] = useState<OpportunityStage | null>(
    null,
  );
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const [isRulesModalOpen, setIsRulesModalOpen] = useState(false);
  const [selectedStageForRules, setSelectedStageForRules] =
    useState<OpportunityStage | null>(null);

  useEffect(() => {
    if (isOpen && slug) {
      fetchStages();
    }
  }, [isOpen, slug, pipelineId]);

  const fetchStages = async () => {
    try {
      setLoading((prev) => ({ ...prev, stages: true }));
      const response = await opportunityAPI.getStages({
        slug,
        is_active: true,
        pipeline_id: pipelineId,
      });
      setStages(
        response.data.sort((a, b) => a.order_position - b.order_position),
      );
    } catch (error: any) {
      toast({
        title: "Error al cargar etapas",
        description: error.message || "No se pudieron cargar las etapas",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, stages: false }));
    }
  };

  const handleOpenFormModal = (level: number) => {
    setSelectedGroupLevel(level);
    setEditingStage(null);
    setIsFormModalOpen(true);
  };

  const handleEditStage = (stage: OpportunityStage) => {
    setEditingStage(stage);
    setIsFormModalOpen(true);
  };

  const handleSaveStage = async (stageData: {
    name: string;
    description?: string;
    color: string;
    level: number;
    orden: number;
    stagnation_config?: StageStagnationConfig | null;
    visibility_config?: StageVisibilityConfig | null;
  }) => {
    console.log("💾 handleSaveStage ejecutado", {
      stageData,
      pipelineId,
      isEdit: !!editingStage,
    });

    try {
      setLoading((prev) => ({ ...prev, create: true }));

      const finalStageType = getStageTypeFromLevel(
        stageData.level,
        stageData.name,
      );

      console.log(
        `[DEBUG] Determined stage type: ${finalStageType} for stage name "${stageData.name}"`,
      );

      if (editingStage) {
        const updateData = {
          slug: slug,
          name: stageData.name.trim(),
          color: stageData.color,
          stage_type: finalStageType,
          order_position: stageData.orden,
          is_active: true,
          description: stageData.description?.trim(),
          pipeline_id: pipelineId,
          stagnation_config: stageData.stagnation_config,
          visibility_config: stageData.visibility_config,
        };

        console.log("🔄 Actualizando etapa con datos:", updateData);

        const response = await opportunityAPI.updateStage(
          editingStage.id,
          updateData,
        );

        toast({
          title: "¡Etapa actualizada!",
          description: `La etapa "${stageData.name}" se ha actualizado correctamente`,
        });

        onStageUpdated?.();

        setStages((prev) =>
          prev.map((s) => (s.id === editingStage.id ? response.data : s)),
        );
      } else {
        if (!pipelineId) {
          console.error(
            "❌ Error: pipelineId es undefined o null al intentar crear etapa",
          );
          toast({
            title: "Error",
            description: "No se puede crear una etapa sin un pipeline asignado",
            variant: "destructive",
          });
          return;
        }

        const nextOrderPosition =
          stages.length > 0
            ? Math.max(...stages.map((s) => s.order_position)) + 1
            : 1;

        const createData = {
          slug,
          name: stageData.name.trim(),
          color: stageData.color,
          order_position: nextOrderPosition,
          stage_type: finalStageType,
          pipeline_id: pipelineId,
          is_active: true,
          description: stageData.description?.trim(),
          stagnation_config: stageData.stagnation_config,
          visibility_config: stageData.visibility_config,
        };

        console.log("🚀 Enviando solicitud de creación de etapa:", createData);

        const response = await opportunityAPI.createStage(createData);

        console.log("✅ Etapa creada exitosamente:", response.data);

        toast({
          title: "¡Etapa creada!",
          description: `La etapa "${stageData.name}" se ha creado correctamente`,
        });

        onStageCreated?.();

        setStages((prev) =>
          [...prev, response.data].sort(
            (a, b) => a.order_position - b.order_position,
          ),
        );
      }

      setIsFormModalOpen(false);
      setEditingStage(null);
    } catch (error: any) {
      console.error("❌ Error en handleSaveStage:", error);
      toast({
        title: editingStage
          ? "Error al actualizar etapa"
          : "Error al crear etapa",
        description: error.message || "Ha ocurrido un error",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, create: false }));
    }
  };

  const confirmDeleteStage = (stage: OpportunityStage) => {
    setStageToDelete(stage);
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteStage = async () => {
    if (!stageToDelete) return;

    try {
      setLoading((prev) => ({ ...prev, delete: true }));
      await opportunityAPI.deleteStage(stageToDelete.id, slug);

      toast({
        title: "¡Etapa eliminada!",
        description: "La etapa se ha eliminado correctamente",
      });

      onStageDeleted?.();

      setStages((prev) => prev.filter((s) => s.id !== stageToDelete.id));
      setIsDeleteConfirmOpen(false);
      setStageToDelete(null);
    } catch (error: any) {
      toast({
        title: "Error al eliminar etapa",
        description: error.message || "No se pudo eliminar la etapa",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, delete: false }));
    }
  };

  const handleMoveStage = async (
    stage: OpportunityStage,
    direction: "up" | "down",
  ) => {
    const group = LEVEL_GROUPS.find((g) =>
      g.stage_types.includes(stage.stage_type),
    );
    if (!group) return;

    const groupStages = stages
      .filter((s) => group.stage_types.includes(s.stage_type))
      .sort((a, b) => a.order_position - b.order_position);

    const currentIndex = groupStages.findIndex((s) => s.id === stage.id);
    if (currentIndex === -1) return;

    const targetIndex =
      direction === "up" ? currentIndex - 1 : currentIndex + 1;
    const targetStage = groupStages[targetIndex];

    if (!targetStage) return;

    try {
      // Optimistic update
      const newStages = stages.map((s) => {
        if (s.id === stage.id)
          return { ...s, order_position: targetStage.order_position };
        if (s.id === targetStage.id)
          return { ...s, order_position: stage.order_position };
        return s;
      });

      setStages(newStages.sort((a, b) => a.order_position - b.order_position));

      await Promise.all([
        opportunityAPI.updateStage(stage.id, {
          slug,
          order_position: targetStage.order_position,
        }),
        opportunityAPI.updateStage(targetStage.id, {
          slug,
          order_position: stage.order_position,
        }),
      ]);

      onStageUpdated?.();
    } catch (error: any) {
      console.error("Error moving stage:", error);
      fetchStages();
      toast({
        title: "Error",
        description: "No se pudo mover la etapa",
        variant: "destructive",
      });
    }
  };

  const handleCloseFormModal = () => {
    setIsFormModalOpen(false);
    setEditingStage(null);
  };

  const groupedStages = LEVEL_GROUPS.map((group) => ({
    ...group,
    stages: stages
      .filter((s) => group.stage_types.includes(s.stage_type))
      .sort((a, b) => a.order_position - b.order_position),
  }));

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center justify-between">
              <span>Gestión de Etapas</span>
              {loading.stages && <Loader2 className="h-4 w-4 animate-spin" />}
            </DialogTitle>
            <DialogDescription>
              Arrastra las etapas para organizarlas, haz clic para editarlas o
              crea nuevas.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {groupedStages.map((group) => (
              <Card
                key={group.level}
                className="border-slate-200 bg-slate-50/50"
              >
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center justify-between">
                    <span className="text-lg font-semibold text-slate-800">
                      {group.label}
                    </span>
                    <Badge variant="secondary" className="font-normal">
                      {group.stages.length} etapa
                      {group.stages.length !== 1 ? "s" : ""}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 min-h-[60px]">
                    {group.stages.map((stage, index) => (
                      <div
                        key={stage.id}
                        className="group flex items-center justify-between rounded-lg bg-white p-3 shadow-sm transition-all border-l-4 hover:shadow-md hover:border-blue-400"
                        style={{ borderColor: stage.color }}
                      >
                        <div className="flex items-center flex-1 gap-3">
                          <div
                            className="flex-1 cursor-pointer min-w-0"
                            onClick={() => handleEditStage(stage)}
                            title={`Haz clic para editar "${stage.name}"`}
                          >
                            <p className="font-medium text-slate-800 transition-colors group-hover:text-blue-700 truncate">
                              {stage.name}
                            </p>
                            <p className="text-xs text-slate-500 truncate">
                              {stage.description || "Sin descripción"}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          <div className="flex flex-col sm:flex-row gap-1 mr-2 border-r pr-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-slate-400 hover:text-slate-700"
                              onClick={() => handleMoveStage(stage, "up")}
                              disabled={index === 0}
                              title="Mover arriba"
                            >
                              <ArrowUp className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-slate-400 hover:text-slate-700"
                              onClick={() => handleMoveStage(stage, "down")}
                              disabled={index === group.stages.length - 1}
                              title="Mover abajo"
                            >
                              <ArrowDown className="h-4 w-4" />
                            </Button>
                          </div>

                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                            onClick={() => handleEditStage(stage)}
                            disabled={loading.delete}
                            title={`Editar etapa "${stage.name}"`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-slate-500 hover:bg-red-100 hover:text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmDeleteStage(stage);
                            }}
                            disabled={loading.delete}
                            title={`Eliminar etapa "${stage.name}"`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-4 w-full border-dashed border-slate-300 hover:border-solid hover:bg-slate-100 hover:border-blue-400 text-slate-600"
                    onClick={() => handleOpenFormModal(group.level)}
                    disabled={loading.create}
                    title={`Agregar nueva etapa al grupo ${group.label}`}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar etapa
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <DialogFooter className="border-t pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading.create || loading.delete}
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Confirmar Eliminación
            </DialogTitle>
            <DialogDescription className="sr-only">
              Confirmación para eliminar la etapa {stageToDelete?.name}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                ¿Estás seguro de que deseas eliminar la etapa{" "}
                <strong>"{stageToDelete?.name}"</strong>?
              </AlertDescription>
            </Alert>

            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="h-4 w-4 rounded flex-shrink-0"
                  style={{ backgroundColor: stageToDelete?.color }}
                />
                <div>
                  <p className="font-medium text-slate-800">
                    {stageToDelete?.name}
                  </p>
                  <p className="text-sm text-slate-500">
                    Grupo:{" "}
                    {
                      LEVEL_GROUPS.find((g) =>
                        g.stage_types.includes(stageToDelete?.stage_type || ""),
                      )?.label
                    }
                  </p>
                </div>
              </div>

              {stageToDelete?.description && (
                <p className="text-sm text-slate-600 mt-2">
                  {stageToDelete.description}
                </p>
              )}
            </div>

            <div className="text-sm text-slate-600">
              <p>Esta acción no se puede deshacer.</p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteConfirmOpen(false)}
              disabled={loading.delete}
              className="w-full sm:w-auto"
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteStage}
              disabled={loading.delete}
              className="w-full sm:w-auto"
            >
              {loading.delete ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Sí, eliminar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isFormModalOpen && (
        <StageFormModal
          isOpen={isFormModalOpen}
          onClose={handleCloseFormModal}
          onSave={handleSaveStage}
          initialStage={
            editingStage
              ? {
                  name: editingStage.name,
                  color: editingStage.color,
                  level: STAGE_TYPE_TO_LEVEL[editingStage.stage_type] || 1,
                  orden: editingStage.order_position,
                  stagnation_config: editingStage.stagnation_config
                    ? {
                        value: editingStage.stagnation_config.value,
                        unit: editingStage.stagnation_config.unit,
                        action: editingStage.stagnation_config.action as any,
                      }
                    : null,
                  visibility_config: editingStage.visibility_config
                    ? {
                        value: editingStage.visibility_config.value,
                        unit: editingStage.visibility_config.unit as any,
                      }
                    : null,
                }
              : undefined
          }
          isEdit={!!editingStage}
          defaultLevel={selectedGroupLevel}
        />
      )}
    </>
  );
}
